import React, { useState, useEffect } from "react";
import { Eye, XCircle, CheckCircle2 } from "lucide-react";

// Regex chỉ nhận diện blank ___, không nhận (9) nữa
const BLANK_REGEX_SPLIT = /(_{3,})/g;
const BLANK_REGEX_TEST = /(_{3,})/;

const SynthesisMatrixQuestion = ({
  allQuestions = [],
  questions = [],
  results = [],
  currentAnswers = {},
  submittedFirstTime = false,
  canRetry = false,
  isDisabled = false,
  onAnswerChange,
}) => {

  // state mới: lưu ID câu đang mở Explanation
  const [expandedExplanationId, setExpandedExplanationId] = useState(null);
  const [matrixQuestions, setMatrixQuestions] = useState([]);

  const toggleExplanation = (id) => {
    setExpandedExplanationId((prev) => (prev === id ? null : id));
  };

  /** ------------------ Extractors ------------------ **/

  const extractMainIdea = (text) => {
    if (!text) return "";
    text = text.replace(/\(\d+\)/g, ""); 
    const idx = text.indexOf("(");
    if (idx === -1) {
      const colon = text.indexOf(":");
      return colon !== -1 ? text.substring(0, colon).trim() : text.trim();
    }
    return text.substring(0, idx).trim();
  };

  const extractAuthor = (text) => {
    const match = text.match(/\((.*?)\)/);
    if (!match) return null;

    const inner = match[1].trim();
    if (!inner || inner.toLowerCase().includes("row header")) return null;
    if (!/\d{4}/.test(inner)) return null;

    return inner;
  };

  const extractContent = (text) => {
    text = text.replace(/\(\d+\)/g, "");
    const parts = text.split(":");
    return parts[1]?.trim() || "";
  };

  /** ------------------------------------------------ **/

  useEffect(() => {
    const source = allQuestions.length ? allQuestions : questions;
    if (Array.isArray(source)) {
      const filtered = source.filter(
        (q) => q.question_type === "synthesis_matrix"
      );
      setMatrixQuestions(filtered);
    }
  }, [allQuestions, questions]);

  const getQuestionResult = (id) =>
    results?.find((r) => r.id === id) || null;

  const isInputDisabled = (id) => {
    if (isDisabled) return true;
    if (!submittedFirstTime) return false;

    const r = getQuestionResult(id);
    if (!r) return false;

    if (r.isCorrect) return true;
    if (!canRetry) return true;

    return false;
  };

  const handleInput = (id, value) => {
    const r = getQuestionResult(id);
    const retryMode =
      submittedFirstTime && r && !r.isCorrect && canRetry;

    if (typeof onAnswerChange === "function") {
      onAnswerChange(id, value, retryMode);
    }
  };

  if (!matrixQuestions.length) {
    return (
      <div className="p-6 text-center text-gray-500">
        No synthesis matrix questions available.
      </div>
    );
  }

  /** ----------------- Grouping ----------------- **/

  const grouped = {};

  matrixQuestions.forEach((q) => {
    const mainIdea = extractMainIdea(q.question_text);
    const author = extractAuthor(q.question_text);
    const content = extractContent(q.question_text);

    if (!grouped[mainIdea]) grouped[mainIdea] = {};
    grouped[mainIdea][author] = { ...q, content };
  });

  const uniqueAuthors = [
    ...new Set(
      matrixQuestions.map((q) => extractAuthor(q.question_text)).filter(Boolean)
    )
  ];

  /** ------------------------------------------------ **/

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="overflow-x-auto mb-6">

        <table className="w-full border-collapse border-2 border-gray-800">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-2 border-gray-800 p-3 text-left font-bold w-[25%]">
                Main ideas
              </th>

              {uniqueAuthors.map((author) => (
                <th
                  key={author}
                  className="border-2 border-gray-800 p-3 text-left font-bold w-[37.5%]"
                >
                  {author}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Object.entries(grouped).map(([mainIdea, authors], rowIdx) => {

              const qMain = authors[null] || null;
              const resultMain = qMain ? getQuestionResult(qMain.id) : null;

              const cleanMainIdea = mainIdea.replace(/\(\d+\)/g, "");
              const mainIdeaParts = cleanMainIdea.split(BLANK_REGEX_SPLIT);

              return (
                <React.Fragment key={rowIdx}>
                  <tr className="align-top hover:bg-gray-50">

                    {/* ================= MAIN IDEA COLUMN ================= */}
                    <td className="border border-gray-800 px-4 py-3 bg-gray-50 leading-relaxed">
                      <div className="flex flex-col gap-2">

                        {/* text + input */}
                        <div>
                          {mainIdeaParts.map((p, idx) =>
                            BLANK_REGEX_TEST.test(p) && qMain ? (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-2 align-middle"
                              >
                                <span className="text-blue-600 font-semibold">
                                  ({qMain.question_number})
                                </span>

                                <input
                                  type="text"
                                  className={`px-2 py-1 border rounded w-24 text-center ${
                                    isInputDisabled(qMain.id)
                                      ? "bg-gray-100 cursor-not-allowed"
                                      : "border-blue-300"
                                  }`}
                                  disabled={isInputDisabled(qMain.id)}
                                  value={currentAnswers[`q${qMain.id}`] || ""}
                                  onChange={(e) =>
                                    handleInput(qMain.id, e.target.value)
                                  }
                                />
                              </span>
                            ) : (
                              <span key={idx}>{p}</span>
                            )
                          )}
                        </div>

                        {/* Correct / Incorrect + Explanation */}
                        {submittedFirstTime && resultMain && (
                          <div className="flex items-center gap-3 text-xs">

                            {resultMain.isCorrect ? (
                              <span className="px-2 py-[2px] bg-green-100 text-green-700 rounded flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Correct
                              </span>
                            ) : (
                              <span className="px-2 py-[2px] bg-red-100 text-red-600 rounded flex items-center gap-1">
                                <XCircle className="w-3 h-3" />
                                Incorrect
                              </span>
                            )}

                            <button
                              onClick={() => toggleExplanation(qMain.id)}
                              className="px-2 py-[2px] bg-blue-100 text-blue-600 rounded hover:bg-blue-200 flex items-center gap-1"
                            >
                              <Eye className="w-3 h-3" />
                              Explanation
                            </button>
                          </div>
                        )}

                        {/* Explanation block (ONLY for this question) */}
                        {expandedExplanationId === qMain?.id && (
                          <div className="mt-2 p-3 rounded border bg-blue-50 text-sm leading-relaxed">
                            <span className="font-bold">Explanation:</span>{" "}
                            {qMain.answer_explanation}
                          </div>
                        )}

                      </div>
                    </td>

                    {/* ================= AUTHOR COLUMNS ================= */}
                    {uniqueAuthors.map((author, colIdx) => {
                      const q = authors[author] || null;

                      if (!q)
                        return (
                          <td
                            key={colIdx}
                            className="border border-gray-800 px-4 py-3 italic text-gray-400"
                          >
                            —
                          </td>
                        );

                      const cleanContent = q.content.replace(/\(\d+\)/g, "");
                      const parts = cleanContent.split(BLANK_REGEX_SPLIT);
                      const result = getQuestionResult(q.id);

                      return (
                        <td
                          key={colIdx}
                          className="border border-gray-800 px-4 py-3 align-top"
                        >
                          <div className="flex flex-col gap-2 leading-relaxed">

                            {/* text + input */}
                            <div>
                              {parts.map((part, i) => {
                                if (BLANK_REGEX_TEST.test(part)) {
                                  return (
                                    <span
                                      key={i}
                                      className="inline-flex items-center gap-2 align-middle"
                                    >
                                      <span className="text-blue-600 font-semibold">
                                        ({q.question_number})
                                      </span>

                                      <input
                                        type="text"
                                        className={`px-2 py-1 border rounded w-24 text-center ${
                                          isInputDisabled(q.id)
                                            ? "bg-gray-100 cursor-not-allowed"
                                            : "border-blue-300"
                                        }`}
                                        disabled={isInputDisabled(q.id)}
                                        value={currentAnswers[`q${q.id}`] || ""}
                                        onChange={(e) =>
                                          handleInput(q.id, e.target.value)
                                        }
                                      />
                                    </span>
                                  );
                                }

                                return <span key={i}>{part}</span>;
                              })}
                            </div>

                            {/* Correct / Incorrect + Explanation */}
                            {submittedFirstTime && result && (
                              <div className="flex items-center gap-3 text-xs">

                                {result.isCorrect ? (
                                  <span className="px-2 py-[2px] bg-green-100 text-green-700 rounded flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Correct
                                  </span>
                                ) : (
                                  <span className="px-2 py-[2px] bg-red-100 text-red-600 rounded flex items-center gap-1">
                                    <XCircle className="w-3 h-3" />
                                    Incorrect
                                  </span>
                                )}

                                <button
                                  onClick={() => toggleExplanation(q.id)}
                                  className="px-2 py-[2px] bg-blue-100 text-blue-600 rounded hover:bg-blue-200 flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  Explanation
                                </button>
                              </div>
                            )}

                            {/* Explanation for this specific question */}
                            {expandedExplanationId === q.id && (
                              <div className="mt-2 p-3 rounded border bg-blue-50 text-sm leading-relaxed">
                                <span className="font-bold">Explanation:</span>{" "}
                                {q.answer_explanation}
                              </div>
                            )}

                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}

          </tbody>
        </table>

      </div>
    </div>
  );
};

export default SynthesisMatrixQuestion;
